import { App, View } from "@pimo/pimo-app-builder";
import { DragAndDropLayout, PMLineChartCard } from "@pimo/pimo-components";
import { getLocalizedMessage } from "@pimo/pimo-l10n";

import { TestAppState } from "../../../app";
import { getLocalizedCardCopy } from "../../../utils/get-localized-card-copy";
import {
  getItemName,
  getPluralOfItemName,
} from "../../../utils/get-plural-of-item-name";
import { getProjectName } from "../../../utils/get-plural-of-project-name";
import {
  getCompletedItemsDataAndCategories,
  TIMEFRAME_OPTIONS,
} from "../../../utils/getCompletedItemsByTimeframe";
import { isFieldEnabled } from "../../field-enabled";
import { makeGetCardCopy } from "../card-copy";

/**
 * Adds the completed-items-over-time and the accumulated-completed-items-
 * over-time line charts, in that order (#2222).
 */
export function addCompletedItemsOverTimeCards(
  view: View<TestAppState, DragAndDropLayout>,
  app: App<TestAppState>
) {
  const language = app.getAppState().language || "en";
  const getCardCopy = makeGetCardCopy(app);

  const completedItemsOverTimeComponent = view.addComponent({
    component: PMLineChartCard,
    layoutProps: {
      componentId: "completedItemsOverTimeComponent",
      labels: ["lineChart", "chart", "completedItemsOverTime"],
      description: getCardCopy(language, "reportCompletedItemsOverTime")
        .description,
      title: getCardCopy(language, "reportCompletedItemsOverTime").title,
    },
  });

  completedItemsOverTimeComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "completedItemsOverTimeComponent",
      labels: ["lineChart", "chart", "completedItemsOverTime"],
      title: getCardCopy(language, "reportCompletedItemsOverTime").title,
      description: getCardCopy(language, "reportCompletedItemsOverTime")
        .description,
    };
  });

  completedItemsOverTimeComponent.mapState((appState) => {
    const language = appState.language || "en";
    const completedItemsOverReportingCycle =
      appState.currentReport?.project?._calculated
        ?.completedItemsOverReportingCycle;
    const { data, categories } = getCompletedItemsDataAndCategories(
      completedItemsOverReportingCycle
    );

    return {
      showInfoIcon: false,
      showMenuIcon: false,
      title: getLocalizedCardCopy(language, "reportCompletedItemsOverTime", {
        itemName: getItemName(
          appState.settings?.configuration?.itemName,
          language
        ),
        itemNamePlural: getPluralOfItemName(
          appState.settings?.configuration?.itemName,
          language
        ),
        projectName: getProjectName(
          appState.settings?.configuration?.projectName,
          language
        ),
      }).title,
      timespans: TIMEFRAME_OPTIONS,
      data,
      categories,
      xLabel: getLocalizedMessage(language, "time") || "Time",
      yLabel:
        getLocalizedMessage(language, "completedItems") || "Completed Items",
    };
  });

  completedItemsOverTimeComponent.mapVisibility(({ settings }) =>
    isFieldEnabled(settings, "completion", "itemReporting")
  );

  const accumulatedCompletedItemsOverTimeComponent = view.addComponent({
    component: PMLineChartCard,
    layoutProps: {
      componentId: "accumulatedCompletedItemsOverReportingCycle",
      labels: ["lineChart", "chart", "completedItemsOverTime"],
      description: getCardCopy(
        language,
        "reportCompletedItemsOverTimeCumulative"
      ).description,
      title: getCardCopy(language, "reportCompletedItemsOverTimeCumulative")
        .title,
    },
  });

  accumulatedCompletedItemsOverTimeComponent.mapLayoutProps(({ language }) => {
    return {
      componentId: "accumulatedCompletedItemsOverReportingCycle",
      labels: ["lineChart", "chart", "completedItemsOverTime"],
      title: getCardCopy(language, "reportCompletedItemsOverTimeCumulative")
        .title,
      description: getCardCopy(
        language,
        "reportCompletedItemsOverTimeCumulative"
      ).description,
    };
  });

  accumulatedCompletedItemsOverTimeComponent.mapState((appState) => {
    const language = appState.language || "en";
    const accumulatedCompletedItemsOverReportingCycle =
      appState.currentReport?.project?._calculated
        ?.accumulatedCompletedItemsOverReportingCycle;
    const { data, categories } = getCompletedItemsDataAndCategories(
      accumulatedCompletedItemsOverReportingCycle
    );

    return {
      showInfoIcon: false,
      showMenuIcon: false,
      title: getLocalizedCardCopy(
        appState.language || "en",
        "reportCompletedItemsOverTimeCumulative",
        {
          itemName: getItemName(
            appState.settings?.configuration?.itemName,
            appState.language || "en"
          ),
          itemNamePlural: getPluralOfItemName(
            appState.settings?.configuration?.itemName,
            appState.language || "en"
          ),
          projectName: getProjectName(
            appState.settings?.configuration?.projectName,
            appState.language || "en"
          ),
        }
      ).title,
      timespans: TIMEFRAME_OPTIONS,
      data,
      categories,
      xLabel: getLocalizedMessage(language, "time") || "Time",
      yLabel:
        getLocalizedMessage(language, "completedItems") || "Completed Items",
    };
  });

  accumulatedCompletedItemsOverTimeComponent.mapVisibility(({ settings }) =>
    isFieldEnabled(settings, "completion", "itemReporting")
  );
}
